import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiError } from '../api/client';
import { api, type AdminAuditEntryDto } from '../api/client';
import { useSession } from '../store/session';
import { loginOf } from '../login';

/**
 * Журнал действий отдельной страницей, а не блоком в админке: бесконечная
 * прокрутка не уживается с реестром и подбором на том же экране — до них
 * пришлось бы пролистывать весь журнал.
 */
export function AdminAudit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready } = useSession();

  const [scope, setScope] = useState<'admin' | 'all'>('admin');
  const [items, setItems] = useState<AdminAuditEntryDto[]>([]);
  // undefined — ещё не грузили, null — журнал кончился.
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sentinel = useRef<HTMLDivElement | null>(null);
  // Курсор, фильтр и признак загрузки дублируются в ref: наблюдатель создаётся
  // один раз на страницу и по состоянию из замыкания дочитывал бы журнал со
  // старого места. Флаг занятости нужен именно синхронный — первый запрос и
  // срабатывание наблюдателя случаются до перерисовки и иначе задвоятся.
  const cursorRef = useRef<string | null | undefined>(undefined);
  const busyRef = useRef(false);
  const scopeRef = useRef<'admin' | 'all'>('admin');
  // Номер прогона: переключение фильтра начинает новый, и ответы прошлого
  // отбрасываются целиком — вместе со своим «загрузка закончилась».
  const runRef = useRef(0);

  const loadMore = useCallback(() => {
    if (busyRef.current || cursorRef.current === null) return;
    const from = cursorRef.current;
    const run = runRef.current;

    busyRef.current = true;
    setBusy(true);
    setError(null);
    void api
      .adminAudit(scopeRef.current, from)
      .then((page) => {
        if (runRef.current !== run) return;
        cursorRef.current = page.nextCursor;
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch((e: ApiError) => {
        if (runRef.current === run) setError(e.message);
      })
      .finally(() => {
        if (runRef.current !== run) return;
        busyRef.current = false;
        setBusy(false);
      });
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/admin/log');
      return;
    }
    if (user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadMore();
  }, [ready, user, navigate, loadMore]);

  const switchScope = (next: 'admin' | 'all') => {
    if (next === scope) return;
    // Ответ уже отправленного запроса отбросится по номеру прогона, поэтому
    // занятость снимаем сразу: новый фильтр не должен ждать чужую страницу.
    runRef.current += 1;
    busyRef.current = false;
    cursorRef.current = undefined;
    scopeRef.current = next;
    setScope(next);
    setItems([]);
    setCursor(undefined);
    loadMore();
  };

  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      // Дочитываем до того, как хвост списка окажется на экране.
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  const when = (iso: string) =>
    new Date(iso).toLocaleString(locale === 'kk' ? 'kk-KZ' : 'ru-RU', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });

  // Коды действий содержат точку, а i18next читает её как вложенность —
  // отсюда подчёркивание. Неизвестный код показываем как есть: журнал не
  // должен молчать о действии только потому, что для него нет подписи.
  const label = (action: string) =>
    t(`audit.actions.${action.replace(/\./g, '_')}`, { defaultValue: action });

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('audit.title')}</h1>
        <p className="muted">{t('audit.lead')}</p>
        <div className="btn-row">
          <button
            className={`btn ${scope === 'admin' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => switchScope('admin')}
          >
            {t('audit.scopeAdmin')}
          </button>
          <button
            className={`btn ${scope === 'all' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => switchScope('all')}
          >
            {t('audit.scopeAll')}
          </button>
        </div>
        <Link to="/admin" className="fine">
          ← {t('nav.admin')}
        </Link>
      </div>

      <div className="card log">
        {items.map((entry) => (
          <div className="log-row" key={entry.id}>
            <div className="log-head">
              <strong>{label(entry.action)}</strong>
              <span className="fine">{when(entry.at)}</span>
            </div>

            <p className="fine">
              {entry.actor ? `${entry.actor.name} · ${loginOf(entry.actor)}` : t('audit.actorGone')}
            </p>

            {entry.pair && (
              <>
                <p className="fine">
                  {t('audit.pair')}: {entry.pair.a}
                  {entry.pair.b ? ` · ${entry.pair.b}` : ` · ${t('audit.pairPending')}`}
                </p>
                <p className="fine">
                  <a href={entry.pair.url} target="_blank" rel="noopener noreferrer">
                    {entry.pair.url}
                  </a>
                </p>
              </>
            )}

            {entry.subjectUser && (
              <p className="fine">
                {t('audit.subject')}: {entry.subjectUser.name} · {loginOf(entry.subjectUser)}
              </p>
            )}

            {entry.meta && Object.keys(entry.meta).length > 0 && (
              <p className="fine log-meta">
                {Object.entries(entry.meta)
                  .filter(([, v]) => v !== null && v !== undefined && v !== '')
                  .map(([k, v]) => `${k}: ${String(v)}`)
                  .join(' · ')}
              </p>
            )}
          </div>
        ))}

        {busy && <p className="muted">{t('common.loading')}</p>}
        {error && (
          <p className="fine" style={{ color: 'var(--alert)' }}>
            {error}
          </p>
        )}
        {!busy && !error && items.length === 0 && <p className="notice">{t('audit.empty')}</p>}
        {!busy && cursor === null && items.length > 0 && <p className="fine">{t('audit.end')}</p>}
      </div>

      {/* Наблюдатель следит за этой точкой: попала в поле зрения — дочитываем. */}
      <div ref={sentinel} aria-hidden="true" />
    </div>
  );
}
